import React, { useState } from 'react';
import Button from '@atlaskit/button/standard-button';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { Maybe, Promotion } from '@css-xp/graphtypes';
import { getPromotionTitle, renderPromotionValue } from '../../helper';
import './active-promotion-section.scss';

export type ActivePromotion = Promotion & { isRemoved?: boolean };

const ActivePromotionBox = ({
  promotion,
  index,
  updatePromotionsToRemove,
  enableIsRemoved,
}: {
  promotion: Promotion | undefined | null;
  index: number;
  updatePromotionsToRemove: (id: string, index: string, isRemoved: boolean, promotionType: string) => void;
  enableIsRemoved?: boolean | undefined;
}) => {
  const [isRemoved, setIsRemoved] = useState<boolean>(enableIsRemoved ?? false);
  const handleRemove = () => {
    setIsRemoved((prevIsRemoved) => {
      updatePromotionsToRemove(promotion?.id as string, index.toString(), !prevIsRemoved, promotion?.type as string);
      return !prevIsRemoved;
    });
  };

  return (
    <div
      className={`active-promotion-box mt-8${isRemoved || enableIsRemoved ? ' removed' : ''}`}
      data-testid={`index-${index}`}
    >
      <h4 className="mb-8">{getPromotionTitle(promotion, { handleEmptyTitle: true })}</h4>
      <div className="remove-promotion-link-container">
        <p>{renderPromotionValue(promotion, { showDuration: true, showReason: true })}</p>
        {enableIsRemoved || (
          <div className="remove-promotion-button">
            <Button
              appearance="link"
              testId={`remove-promotion-${promotion?.id ?? index}`}
              onClick={() => handleRemove()}
            >
              {isRemoved ? 'Undo removal' : 'Remove'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ActivePromotionSection = ({
  promotions,
  updatePromotionsToRemove,
  heading,
  tooltip,
  enableIsRemoved,
}: {
  promotions: ActivePromotion[] | null | undefined;
  updatePromotionsToRemove: (promotionId: string, index: string, isRemoved: boolean, promotionType: string) => void;
  heading?: string | undefined | null;
  tooltip?: string | undefined | null;
  enableIsRemoved?: boolean | undefined;
}) => {
  if (!promotions?.length) {
    return null;
  }

  return (
    <div className="active-promotion-section">
      <h3>
        <span className="mr-4 active-promotion-heading">{heading ? heading : 'Current discounts'}</span>
        {tooltip && (
          <Tooltip content={tooltip} tag="span">
            <InfoIcon size="small" label="active-promotion-info" />
          </Tooltip>
        )}
      </h3>
      {promotions.map((promotion: ActivePromotion | undefined | null, index: number) => {
        return (
          <ActivePromotionBox
            promotion={promotion}
            key={promotion?.id}
            index={index}
            updatePromotionsToRemove={updatePromotionsToRemove}
            enableIsRemoved={enableIsRemoved || Boolean(promotion?.isRemoved)}
          />
        );
      })}
    </div>
  );
};
